/**
 * PlayAd chat composer: textarea with rotating placeholder · "+" attach menu (Upload, Asset Library,
 * Generated & Uploaded, Creations, Inspiration) · mode menu (Instant / Thinking • level → Thinking Modes) ·
 * image + video model chips (`creatives.models`) · context ring · autonomy ("Auto") menu · send ↑ / stop ■.
 */
import { ArrowUp, Check, Clapperboard, Film, Images, Lightbulb, Image as ImageIcon, Plus, Sparkles, SlidersHorizontal, Upload, X, GalleryVerticalEnd } from "lucide-react";
import { useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode, type Ref } from "react";
import { useNavigate } from "react-router";
import { contextWindowOf, type Autonomy, type ChatAttachment, type ChatMode, type ComposerSettings, type GenModel, type OutgoingAttachment, type ThinkingLevel } from "@/api/chat";
import { useChatModels } from "@/api/chat-hooks";
import { errorMessage, uploadFile } from "@/api/client";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { AssetPicker, type PickedAsset } from "../assets/AssetPicker";
import { Spinner } from "../ui/Feedback";
import { MenuLabel, MenuSeparator, PopoverPanel, Tooltip, usePopover } from "../ui/Overlay";
import { Ring } from "../ui/Page";
import { LibraryPicker, type LibrarySource, type PickedMedia } from "./LibraryPicker";
import { AUTONOMY_LABEL, THINKING_LEVELS, isVideoUrl, useComposerPrefs } from "./util";
import { safePreviewSrc } from "@/lib/safe-url";

export interface ComposerHandle {
  setText: (t: string) => void;
  focus: () => void;
}

export interface ComposerSubmit {
  text: string;
  /** Server attachment refs (kind + id / storageKey). */
  attachments: OutgoingAttachment[];
  /** Same items as chat attachments, for the optimistic user bubble. */
  display: ChatAttachment[];
  composer: ComposerSettings;
}

export interface ComposerSettingsState {
  autonomy: Autonomy;
  mode: ChatMode;
  thinkingLevel: ThinkingLevel;
}

interface PendingAtt {
  key: string;
  name: string;
  preview?: string | null;
  mediaType: "image" | "video";
  status: "uploading" | "ready" | "error";
  progress?: number;
  error?: string;
  out?: OutgoingAttachment;
}

const MAX_ATTACHMENTS = 10;
const MAX_IMAGE = 25 * 1024 * 1024;
const MAX_VIDEO = 250 * 1024 * 1024;
let attSeq = 0;

export interface ComposerProps {
  ref?: Ref<ComposerHandle>;
  brandId: string | null | undefined;
  /** A run is active: placeholder changes and the button becomes Stop when empty. */
  running?: boolean;
  stopping?: boolean;
  placeholders?: string[];
  settings: ComposerSettingsState;
  onSettings: (patch: Partial<ComposerSettingsState>) => void;
  contextTokens?: number;
  contextWindow?: number;
  onSend: (s: ComposerSubmit) => Promise<unknown> | unknown;
  onStop?: () => void;
  compact?: boolean;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
}

export function Composer({
  ref,
  brandId,
  running,
  stopping,
  placeholders = ["Ask anything..."],
  settings,
  onSettings,
  contextTokens = 0,
  contextWindow,
  onSend,
  onStop,
  compact,
  autoFocus,
  className,
  disabled,
}: ComposerProps) {
  const [text, setText] = useState("");
  const [atts, setAtts] = useState<PendingAtt[]>([]);
  const [prefs, setPrefs] = useComposerPrefs();
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const [library, setLibrary] = useState<LibrarySource | null>(null);
  const [dragging, setDragging] = useState(false);
  const [phIdx, setPhIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  const resize = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, compact ? 160 : 220)}px`;
  }, [compact]);

  useImperativeHandle(
    ref,
    () => ({
      setText: (t: string) => {
        setText(t);
        requestAnimationFrame(() => {
          resize();
          const ta = taRef.current;
          if (ta) {
            ta.focus();
            ta.setSelectionRange(t.length, t.length);
          }
        });
      },
      focus: () => taRef.current?.focus(),
    }),
    [resize],
  );

  useEffect(() => {
    if (autoFocus) taRef.current?.focus();
  }, [autoFocus]);
  useEffect(resize, [text, resize]);

  // Rotating placeholder (only while empty and idle).
  const rotating = !running && placeholders.length > 1 && !text;
  useEffect(() => {
    if (!rotating) return;
    const t = window.setInterval(() => setPhIdx((i) => (i + 1) % placeholders.length), 2600);
    return () => window.clearInterval(t);
  }, [rotating, placeholders.length]);
  const placeholder = running ? (stopping ? "Stopping…" : "Replying... send now to apply right away") : (placeholders[phIdx % placeholders.length] ?? "Ask anything...");

  // ── Models ──
  const models = useChatModels(brandId);
  const imageModel = pickModel(models.image, prefs.imageModel);
  const videoModel = pickModel(models.video, prefs.videoModel);
  const videoRes = videoModel ? (prefs.videoResolution && videoModel.resolutions?.includes(prefs.videoResolution) ? prefs.videoResolution : (videoModel.defaultResolution ?? videoModel.resolutions?.[0] ?? null)) : null;

  // ── Attachments ──
  const room = MAX_ATTACHMENTS - atts.length;
  const upload = useCallback(
    async (files: File[]) => {
      const accepted = files.filter((f) => {
        const video = f.type.startsWith("video/");
        const image = f.type.startsWith("image/");
        if (!video && !image) {
          toast({ tone: "warning", title: `${f.name} isn't supported`, body: "Attach PNG, JPEG, WebP, GIF, MP4, MOV or WebM." });
          return false;
        }
        if (f.size > (video ? MAX_VIDEO : MAX_IMAGE)) {
          toast({ tone: "danger", title: `${f.name} is too large`, body: video ? "Videos up to 250 MB." : "Images up to 25 MB." });
          return false;
        }
        return true;
      });
      for (const file of accepted.slice(0, Math.max(0, MAX_ATTACHMENTS - atts.length))) {
        const key = `att-${++attSeq}`;
        const mediaType = file.type.startsWith("video/") ? "video" : "image";
        setAtts((a) => [...a, { key, name: file.name, preview: URL.createObjectURL(file), mediaType, status: "uploading", progress: 0 }]);
        try {
          const up = await uploadFile(file, { brandId, purpose: "chat", onProgress: (p) => setAtts((a) => a.map((x) => (x.key === key ? { ...x, progress: p } : x))) });
          const mt = up.kind === "video" ? "video" : "image";
          setAtts((a) => a.map((x) => (x.key === key ? { ...x, status: "ready", mediaType: mt, out: { kind: "upload", storageKey: up.key, name: file.name, url: up.url, mediaType: mt } } : x)));
        } catch (e) {
          setAtts((a) => a.map((x) => (x.key === key ? { ...x, status: "error", error: errorMessage(e) } : x)));
        }
      }
    },
    [brandId, atts.length],
  );

  const addAssets = (picked: PickedAsset[]) => {
    const media = picked.filter((p) => p.mediaType !== "font").slice(0, room);
    setAtts((a) => [
      ...a,
      ...media.map<PendingAtt>((p) => {
        const mediaType = p.mediaType === "video" || isVideoUrl(p.url) ? "video" : "image";
        return { key: `att-${++attSeq}`, name: p.name ?? "Brand asset", preview: p.url, mediaType, status: "ready", out: { kind: "asset", id: p.id, name: p.name, url: p.url, mediaType } };
      }),
    ]);
  };

  const addLibrary = (picked: PickedMedia[]) => {
    setAtts((a) => [
      ...a,
      ...picked.slice(0, room).map<PendingAtt>((p) => ({
        key: `att-${++attSeq}`,
        name: p.name,
        preview: p.url,
        mediaType: p.mediaType,
        status: "ready",
        out: { kind: p.source === "generated" ? "generated" : p.source === "creations" ? "creation" : "inspiration", id: p.id, name: p.name, url: p.url ?? undefined, mediaType: p.mediaType },
      })),
    ]);
  };

  const removeAtt = (key: string) => setAtts((a) => a.filter((x) => x.key !== key));

  const uploading = atts.some((a) => a.status === "uploading");
  const ready = atts.filter((a) => a.status === "ready" && a.out);
  const canSend = !disabled && !busy && !uploading && (text.trim().length > 0 || ready.length > 0);
  const showStop = !!running && !text.trim() && !ready.length;

  const submit = async () => {
    if (!canSend) return;
    const payload: ComposerSubmit = {
      text: text.trim(),
      attachments: ready.map((a) => a.out!),
      display: ready.map((a) => ({ kind: a.mediaType, url: a.preview ?? a.out?.url, id: a.out?.id, data: { name: a.name, source: a.out?.kind } })),
      composer: {
        mode: settings.mode,
        ...(settings.mode === "thinking" ? { thinkingLevel: settings.thinkingLevel } : {}),
        autonomy: settings.autonomy,
        ...(imageModel ? { imageModel: imageModel.id } : {}),
        ...(videoModel ? { videoModel: videoModel.id } : {}),
        ...(videoRes ? { videoResolution: videoRes } : {}),
      },
    };
    const prevText = text;
    const prevAtts = atts;
    setText("");
    setAtts([]);
    setBusy(true);
    try {
      await onSend(payload);
    } catch (e) {
      setText(prevText);
      setAtts(prevAtts);
      toast({ tone: "danger", title: "Message not sent", body: errorMessage(e) });
    } finally {
      setBusy(false);
      taRef.current?.focus();
    }
  };

  const ctxWindow = contextWindowOf({ contextWindow });
  const ctxPct = Math.min(100, Math.round((Math.max(0, contextTokens) / ctxWindow) * 100));
  const remaining = Math.max(0, ctxWindow - contextTokens);

  return (
    <div
      className={cn(
        "relative rounded-2xl border border-line bg-[#222224] shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] transition-colors focus-within:border-primary/70",
        dragging && "border-primary ring-2 ring-primary/30",
        className,
      )}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        setDragging(false);
        void upload(Array.from(e.dataTransfer.files));
      }}
    >
      {atts.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-3 pt-3 scrollbar-none">
          {atts.map((a) => (
            <AttachmentThumb key={a.key} att={a} onRemove={() => removeAtt(a.key)} />
          ))}
        </div>
      )}
      <div className="relative px-4 pt-3.5">
        {!text && (
          <span key={running ? "run" : phIdx} aria-hidden className="pointer-events-none absolute inset-x-4 top-3.5 animate-fade-in truncate text-[15px] leading-6 text-subtle">
            {placeholder}
          </span>
        )}
        <textarea
          ref={taRef}
          value={text}
          rows={1}
          aria-label="Message Mimic"
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            }
          }}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData.files ?? []);
            if (files.length) {
              e.preventDefault();
              void upload(files);
            }
          }}
          className={cn("relative block w-full resize-none bg-transparent text-[15px] leading-6 text-fg outline-none scrollbar-thin", compact ? "min-h-6" : "min-h-12")}
        />
      </div>
      <div className="flex items-center gap-1 px-2.5 pb-2.5 pt-1.5">
        <AttachMenu
          brandId={brandId}
          full={room <= 0}
          onUpload={() => fileRef.current?.click()}
          onAssets={() => setAssetOpen(true)}
          onLibrary={(s) => setLibrary(s)}
        />
        <ModeMenu mode={settings.mode} level={settings.thinkingLevel} onChange={(mode, thinkingLevel) => onSettings({ mode, ...(thinkingLevel ? { thinkingLevel } : {}) })} />
        <div className="flex min-w-0 items-center gap-1 overflow-hidden">
          {brandId && models.isLoading ? (
            <span className="skeleton h-5 w-28 rounded-md" aria-hidden />
          ) : (
            <>
              {imageModel && (
                <ModelChip
                  label={imageModel.label}
                  icon={<Sparkles className="size-3.5 text-fg/80" />}
                  title="Image model"
                  models={models.image}
                  selected={imageModel.id}
                  onSelect={(id) => setPrefs({ imageModel: id })}
                  compact={compact}
                />
              )}
              {videoModel && (
                <ModelChip
                  label={`${videoModel.label}${videoRes ? ` • ${videoRes}` : ""}`}
                  icon={<Clapperboard className="size-3.5 text-[#4c8dff]" />}
                  title="Video model"
                  models={models.video}
                  selected={videoModel.id}
                  onSelect={(id) => setPrefs({ videoModel: id, videoResolution: null })}
                  resolutions={videoModel.resolutions}
                  resolution={videoRes}
                  onResolution={(r) => setPrefs({ videoResolution: r })}
                  compact={compact}
                />
              )}
            </>
          )}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Tooltip content={`Context Window: ${ctxPct}% Full / ${(remaining / 1000).toFixed(1)}K tokens remaining`}>
            <span className="grid size-7 place-items-center rounded-md" tabIndex={0} aria-label={`Context window ${ctxPct}% full`}>
              <Ring value={ctxPct} size={17} stroke={3} />
            </span>
          </Tooltip>
          <AutonomyMenu value={settings.autonomy} onChange={(autonomy) => onSettings({ autonomy })} />
          {showStop ? (
            <button
              type="button"
              onClick={onStop}
              disabled={stopping}
              aria-label="Stop"
              title="Stop"
              className="grid size-8 place-items-center rounded-lg bg-white text-black transition-transform hover:scale-105 disabled:opacity-60"
            >
              {stopping ? <Spinner size={14} /> : <span className="size-3 rounded-[3px] bg-black" />}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!canSend}
              aria-label={running ? "Send now (applies at the next step)" : "Send"}
              title={running ? "Send now — applied at the next step" : "Send"}
              className={cn("grid size-8 place-items-center rounded-lg transition-colors", canSend ? "bg-primary text-white hover:bg-primary-hover" : "text-subtle")}
            >
              {busy ? <Spinner size={14} /> : <ArrowUp className="size-4.5" strokeWidth={2.2} />}
            </button>
          )}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length) void upload(files);
        }}
      />
      {brandId && (
        <>
          <AssetPicker brandId={brandId} open={assetOpen} onClose={() => setAssetOpen(false)} onPick={addAssets} multiple title="Asset Library" />
          <LibraryPicker brandId={brandId} source={library} open={!!library} onClose={() => setLibrary(null)} onPick={addLibrary} max={Math.max(1, room)} />
        </>
      )}
    </div>
  );
}

function pickModel(list: GenModel[], preferred: string | null): GenModel | null {
  if (!list.length) return null;
  return list.find((m) => m.id === preferred) ?? list.find((m) => m.tags?.includes("default")) ?? list[0] ?? null;
}

/** Thumbnail above the textarea (PlayAd shows attachments as rounded square previews). */
function AttachmentThumb({ att, onRemove }: { att: PendingAtt; onRemove: () => void }) {
  return (
    <div className="group relative shrink-0" title={att.error ?? att.name}>
      <div className={cn("relative size-16 overflow-hidden rounded-xl border bg-[#151517]", att.status === "error" ? "border-danger/70" : "border-line")}>
        {att.preview && att.mediaType === "image" ? (
          <img src={safePreviewSrc(att.preview)} alt={att.name} className="size-full object-cover" />
        ) : att.preview && att.mediaType === "video" && (att.preview.startsWith("blob:") || isVideoUrl(att.preview)) ? (
          <video src={safePreviewSrc(att.preview)} muted playsInline className="size-full object-cover" />
        ) : att.preview ? (
          <img src={safePreviewSrc(att.preview)} alt={att.name} className="size-full object-cover" />
        ) : (
          <span className="grid size-full place-items-center text-subtle">{att.mediaType === "video" ? <Film className="size-5" /> : <ImageIcon className="size-5" />}</span>
        )}
        {att.mediaType === "video" && att.status !== "uploading" && (
          <span className="absolute bottom-1 left-1 grid size-5 place-items-center rounded-full bg-black/60 text-white">
            <Film className="size-2.5" />
          </span>
        )}
        {att.status === "uploading" && (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 text-[10px] font-medium text-white">
            <Spinner size={14} />
            {att.progress !== undefined && att.progress > 0 && att.progress < 1 ? `${Math.round(att.progress * 100)}%` : null}
          </span>
        )}
        {att.status === "error" && <span className="absolute inset-x-0 bottom-0 bg-danger/85 px-1 py-0.5 text-center text-[9.5px] font-semibold text-white">Failed</span>}
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${att.name}`}
        className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full border border-line-strong bg-elevated text-muted opacity-0 transition-opacity hover:text-fg group-hover:opacity-100 focus:opacity-100"
      >
        <X className="size-3" />
      </button>
    </div>
  );
}

function AttachMenu({ brandId, full, onUpload, onAssets, onLibrary }: { brandId: string | null | undefined; full: boolean; onUpload: () => void; onAssets: () => void; onLibrary: (s: LibrarySource) => void }) {
  const p = usePopover();
  const go = (fn: () => void) => () => {
    p.close();
    fn();
  };
  const needBrand = !brandId;
  const rows: { label: string; icon: ReactNode; onClick: () => void; disabled?: boolean }[] = [
    { label: "Upload", icon: <Upload className="size-4.5" strokeWidth={1.75} />, onClick: onUpload },
    { label: "Asset Library", icon: <ImageIcon className="size-4.5" strokeWidth={1.75} />, onClick: onAssets, disabled: needBrand },
    { label: "Generated & Uploaded", icon: <Images className="size-4.5" strokeWidth={1.75} />, onClick: () => onLibrary("generated"), disabled: needBrand },
    { label: "Creations", icon: <GalleryVerticalEnd className="size-4.5" strokeWidth={1.75} />, onClick: () => onLibrary("creations"), disabled: needBrand },
    { label: "Inspiration", icon: <Lightbulb className="size-4.5" strokeWidth={1.75} />, onClick: () => onLibrary("inspiration"), disabled: needBrand },
  ];
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-label="Attach"
        aria-expanded={p.open}
        disabled={full}
        title={full ? "Up to 10 attachments" : "Attach"}
        className={cn("grid size-7 shrink-0 place-items-center rounded-md text-fg/85 transition-colors hover:bg-hover disabled:opacity-40", p.open && "bg-elevated-2 text-fg")}
      >
        <Plus className="size-4.5" />
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} side="top" align="start" offset={10} className="w-[230px] rounded-2xl p-1.5">
        {rows.map((r) => (
          <button
            key={r.label}
            type="button"
            role="menuitem"
            disabled={r.disabled}
            title={r.disabled ? "Pick a brand first" : undefined}
            onClick={go(r.onClick)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] text-fg/90 transition-colors hover:bg-hover disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <span className="text-fg/75">{r.icon}</span>
            {r.label}
          </button>
        ))}
      </PopoverPanel>
    </>
  );
}

/** "Instant" vs "Thinking • <level>" (sliders → Thinking Modes). */
function ModeMenu({ mode, level, onChange }: { mode: ChatMode; level: ThinkingLevel; onChange: (m: ChatMode, l?: ThinkingLevel) => void }) {
  const p = usePopover();
  const levels = usePopover();
  const levelLabel = THINKING_LEVELS.find((l) => l.id === level)?.label ?? "Standard";
  const closeAll = () => {
    levels.close();
    p.close();
  };
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-haspopup="menu"
        aria-expanded={p.open}
        aria-label={`Mode: ${mode === "instant" ? "Instant" : `Thinking, ${levelLabel}`}`}
        className={cn("inline-flex h-7 shrink-0 items-center rounded-md px-2 text-[13px] font-medium text-fg/85 transition-colors hover:bg-hover", p.open && "bg-elevated-2 text-fg")}
      >
        {mode === "instant" ? "Instant" : levelLabel}
      </button>
      <PopoverPanel
        open={p.open}
        onClose={() => {
          if (!levels.open) p.close();
        }}
        anchorRef={p.anchorRef}
        side="top"
        align="start"
        offset={10}
        className="w-[300px] rounded-2xl p-1.5"
      >
        <button
          type="button"
          role="menuitemradio"
          aria-checked={mode === "instant"}
          onClick={() => {
            onChange("instant");
            closeAll();
          }}
          className={cn("flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-left text-[15px] transition-colors hover:bg-hover", mode === "instant" && "bg-elevated/70")}
        >
          Instant
          {mode === "instant" && <Check className="size-4 text-fg/80" />}
        </button>
        <div
          ref={(el) => {
            levels.anchorRef.current = el;
          }}
          className={cn("flex items-center rounded-xl transition-colors hover:bg-hover", (mode === "thinking" || levels.open) && "bg-elevated/70")}
        >
          <button
            type="button"
            role="menuitemradio"
            aria-checked={mode === "thinking"}
            onClick={() => {
              onChange("thinking", level);
              closeAll();
            }}
            className="flex min-w-0 flex-1 items-center gap-2 px-4 py-3.5 text-left text-[15px]"
          >
            <span className="font-medium">Thinking</span>
            <span className="size-1 rounded-full bg-subtle" />
            <span className="text-fg/80">{levelLabel}</span>
          </button>
          <button type="button" onClick={levels.toggle} aria-label="Thinking modes" aria-expanded={levels.open} className="mr-1.5 grid size-9 place-items-center rounded-lg text-fg/80 hover:bg-elevated-2 hover:text-fg">
            <SlidersHorizontal className="size-4.5" />
          </button>
        </div>
      </PopoverPanel>
      <PopoverPanel open={levels.open && p.open} onClose={levels.close} anchorRef={levels.anchorRef} side="right" align="end" offset={14} className="w-[min(380px,calc(100vw-24px))] rounded-2xl p-2">
        <MenuLabel>
          <span className="text-[13px] font-semibold normal-case tracking-normal text-fg">Thinking Modes</span>
        </MenuLabel>
        {THINKING_LEVELS.map((l) => (
          <button
            key={l.id}
            type="button"
            role="menuitemradio"
            aria-checked={mode === "thinking" && l.id === level}
            onClick={() => {
              onChange("thinking", l.id);
              closeAll();
            }}
            className="flex w-full flex-col gap-0.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-hover"
          >
            <span className="flex items-center gap-2 text-[15px] font-medium">
              {l.label}
              {mode === "thinking" && l.id === level && (
                <span className="grid size-5 place-items-center rounded-full bg-white text-black">
                  <Check className="size-3" strokeWidth={3.5} />
                </span>
              )}
            </span>
            <span className="text-[12.5px] leading-snug text-muted">{l.description}</span>
          </button>
        ))}
      </PopoverPanel>
    </>
  );
}

function ModelChip({
  label,
  icon,
  title,
  models,
  selected,
  onSelect,
  resolutions,
  resolution,
  onResolution,
  compact,
}: {
  label: string;
  icon: ReactNode;
  title: string;
  models: GenModel[];
  selected: string;
  onSelect: (id: string) => void;
  resolutions?: string[];
  resolution?: string | null;
  onResolution?: (r: string) => void;
  compact?: boolean;
}) {
  const p = usePopover();
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-label={`${title}: ${label}`}
        aria-expanded={p.open}
        className={cn("inline-flex h-7 min-w-0 items-center gap-1.5 rounded-md px-2 text-[13px] text-fg/85 transition-colors hover:bg-hover", p.open && "bg-hover")}
      >
        <span className="shrink-0">{icon}</span>
        <span className={cn("truncate", compact ? "max-w-[96px]" : "max-w-[190px]")}>{label}</span>
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} side="top" align="start" className="max-h-[380px] w-[320px] overflow-y-auto rounded-2xl p-1.5 scrollbar-thin" role="listbox">
        <MenuLabel>{title}</MenuLabel>
        {models.map((m) => (
          <button
            key={m.id}
            type="button"
            role="option"
            aria-selected={m.id === selected}
            onClick={() => {
              onSelect(m.id);
              if (!m.resolutions?.length) p.close();
            }}
            className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-hover"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">{m.label}</span>
              {m.description && <span className="mt-0.5 line-clamp-2 block text-[11.5px] leading-snug text-muted">{m.description}</span>}
            </span>
            <span className={cn("mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border", m.id === selected ? "border-white bg-white text-black" : "border-line-strong")}>
              {m.id === selected && <Check className="size-2.5" strokeWidth={3.5} />}
            </span>
          </button>
        ))}
        {resolutions && resolutions.length > 0 && onResolution && (
          <>
            <MenuSeparator />
            <div className="flex items-center justify-between gap-2 px-2.5 py-2">
              <span className="text-[12px] text-muted">Resolution</span>
              <div className="flex gap-1">
                {resolutions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => onResolution(r)}
                    aria-pressed={r === resolution}
                    className={cn("h-6 rounded-md px-2 text-[12px] font-medium transition-colors", r === resolution ? "bg-white text-black" : "bg-elevated text-fg/80 hover:bg-elevated-2")}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </PopoverPanel>
    </>
  );
}

const AUTONOMY_OPTIONS: { value: Autonomy; label: string }[] = [
  { value: "confirm", label: "Confirm before running" },
  { value: "auto_ask", label: "Auto-run but ask if unclear" },
  { value: "auto", label: "Auto-run and make all decisions" },
];

function AutonomyMenu({ value, onChange }: { value: Autonomy; onChange: (a: Autonomy) => void }) {
  const p = usePopover();
  const nav = useNavigate();
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-haspopup="menu"
        aria-expanded={p.open}
        aria-label={`Run mode: ${AUTONOMY_OPTIONS.find((o) => o.value === value)?.label}`}
        className={cn("inline-flex h-7 items-center rounded-md px-2 text-[13px] font-medium text-fg/85 transition-colors hover:bg-hover", p.open && "bg-elevated-2 text-fg")}
      >
        {AUTONOMY_LABEL[value]}
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} side="top" align="end" offset={10} className="w-[min(390px,calc(100vw-24px))] rounded-[22px] p-0">
        <div className="px-2 pb-1 pt-3">
          {AUTONOMY_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={o.value === value}
              onClick={() => {
                onChange(o.value);
                p.close();
              }}
              className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-[15px] font-medium transition-colors hover:bg-hover"
            >
              {o.label}
              {o.value === value && (
                <span className="grid size-6 place-items-center rounded-full bg-white text-black">
                  <Check className="size-3.5" strokeWidth={3} />
                </span>
              )}
            </button>
          ))}
          <p className="px-4 pb-3 pt-3 text-[12.5px] leading-relaxed text-muted">All modes still check with you before over-budget spending or deletes.</p>
        </div>
        <div className="border-t border-line px-2 py-2">
          <button
            type="button"
            onClick={() => {
              p.close();
              nav("/decisions");
            }}
            className="flex w-full items-center rounded-xl px-4 py-3 text-left text-[15px] font-medium transition-colors hover:bg-hover"
          >
            Manage approvals
          </button>
        </div>
      </PopoverPanel>
    </>
  );
}
